import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import useEvent from "react-use-event-hook";

import { IGeoJSON } from "@ryde/shared";

import {
  IEditablePolygon,
  IMapLibrePolygonEditorHandle,
} from "./maplibre-polygon-editor.models";
import {
  toEditablePolygons,
  toEditorGeoJson,
} from "./maplibre-polygon-editor.utils";

interface IUseMapLibrePolygonEditorStateArgs {
  /**
   * Seeds the editor once, on mount. Deliberately not watched: re-reading it on every identity change
   * would throw away whatever the user had reshaped the moment the parent re-rendered. Consumers that
   * need to load a different shape remount the editor with a `key`.
   */
  value?: IGeoJSON;
  handleRef?: MutableRefObject<IMapLibrePolygonEditorHandle | undefined>;
}

interface IMapLibrePolygonEditorState {
  polygons: IEditablePolygon[];
  /** The live list, for pointer handlers that are plain DOM listeners and have to read it synchronously. */
  polygonsRef: MutableRefObject<IEditablePolygon[]>;
  commit: (polygons: IEditablePolygon[]) => void;
  createId: () => string;
  /** Bumped whenever shapes arrive from outside — the seed, or an import — so the map refits to them. */
  fitRequestId: number;
}

/**
 * Owns the shapes being edited, the ids that keep them apart, and the imperative handle the surrounding
 * chrome reaches them through.
 *
 * Shapes live in a ref as well as in state for the same reason the lasso's corners do: the pointer
 * handlers below are plain map listeners that need the current geometry synchronously, and reading it
 * inside a `setState` updater would mean mutating from within an updater that has to stay pure.
 */
export const useMapLibrePolygonEditorState = ({
  value,
  handleRef,
}: IUseMapLibrePolygonEditorStateArgs): IMapLibrePolygonEditorState => {
  const [polygons, setPolygons] = useState<IEditablePolygon[]>([]);
  const [fitRequestId, setFitRequestId] = useState(0);
  const polygonsRef = useRef<IEditablePolygon[]>([]);
  const idCounterRef = useRef(0);
  const seedRef = useRef(value);

  const createId = useCallback((): string => {
    idCounterRef.current += 1;

    return `polygon-${idCounterRef.current}`;
  }, []);

  const commit = useCallback((next: IEditablePolygon[]): void => {
    polygonsRef.current = next;
    setPolygons(next);
  }, []);

  const requestFit = useCallback(
    (): void => setFitRequestId(previous => previous + 1),
    [],
  );

  const getGeoJson = useEvent((): IGeoJSON =>
    toEditorGeoJson(polygonsRef.current),
  );

  const addFeatures = useEvent((geoJson: IGeoJSON): void => {
    const added = toEditablePolygons(geoJson, createId);

    if (!added.length) {
      return;
    }

    commit([...polygonsRef.current, ...added]);
    requestFit();
  });

  const clearAll = useEvent((): void => commit([]));

  useEffect(() => {
    const seeded = toEditablePolygons(seedRef.current, createId);

    if (seeded.length) {
      commit(seeded);
      requestFit();
    }
  }, [createId, commit, requestFit]);

  useEffect(() => {
    if (!handleRef) {
      return;
    }

    handleRef.current = { getGeoJson, addFeatures, clearAll };

    return () => {
      handleRef.current = undefined;
    };
  }, [handleRef, getGeoJson, addFeatures, clearAll]);

  return { polygons, polygonsRef, commit, createId, fitRequestId };
};
